/** @format */
import {
  AemPinnedContentResponse,
  AemPinnedContentSpotTranslated,
  PinnedContent,
  PinnedContentRequest,
  PinnedContentSpotItem,
} from '@plp/data-access/interfaces';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { PINNED_CONTENT_ERROR, PINNED_CONTENT_RETRY_COUNT } from '@plp/data-access/constants';

import { HttpErrorResponse } from '@angular/common/http';
import { Logger } from '@shared/data-access/services';
import { ProductListAemDao } from './product-list-aem.dao';
import { catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ProductListAemService {
  private readonly logger = inject(Logger);
  private readonly productListAemDao = inject(ProductListAemDao);

  /**
   * @description Fetches the AEM PCS data for the supplied page context.
   * @param request - PCS lookup parameters.
   * @returns Observable of the PCS response, or `null` when the fetch fails.
   */
  getPinnedContentData$(
    request: PinnedContentRequest,
    retries = PINNED_CONTENT_RETRY_COUNT,
  ): Observable<AemPinnedContentResponse | null> {
    return this.productListAemDao.getPinnedContentData$(request, retries).pipe(
      catchError((error: HttpErrorResponse) => {
        this.logger.error(
          `${PINNED_CONTENT_ERROR}: Error fetching pinned content for pageType: ${request.pageType}`,
          error,
        );
        return of(null);
      }),
    );
  }

  /**
   * @description Translates the AEM PCS response into the internal `PinnedContent`
   * map used by the product grid. Each spot is keyed by its grid index
   * (`pinnedPosition - 1`) to match the legacy Homr shape. Pure and deterministic:
   * empty, missing, or malformed input yields an empty `PinnedContent` object and
   * records with an invalid pinned position are skipped rather than throwing.
   * @param response - AEM PCS response from the AEM data service, or `null`.
   * @returns `PinnedContent` map keyed by internal grid index.
   */
  pinnedContentAemToHomrTranslator(response: AemPinnedContentResponse | null): PinnedContent {
    const items = response?.data?.pinnedContentSpotList?.items;
    const pinnedContent: PinnedContent = {};

    if (!Array.isArray(items)) {
      return pinnedContent;
    }

    for (const spot of items) {
      const pinnedPosition = Number(spot?.pinnedPosition);
      // Grid index is position - 1; skip records without a valid, positive position.
      if (!Number.isInteger(pinnedPosition) || pinnedPosition < 1) {
        continue;
      }
      pinnedContent[pinnedPosition - 1] = this.mapSpotToPinnedContentSpot(spot, pinnedPosition);
    }

    return pinnedContent;
  }

  private mapSpotToPinnedContentSpot(
    spot: PinnedContentSpotItem,
    pinnedPosition: number,
  ): AemPinnedContentSpotTranslated {
    return {
      image: spot?.pcsImage?.desktopImage?._dmS7Url ?? '',
      altText: spot?.pcsImage?.desktopImageAltText ?? '',
      mobileImage: spot?.pcsImage?.mobileImage?._dmS7Url ?? '',
      mobileAltText: spot?.pcsImage?.mobileImageAltText ?? '',
      headline: spot?.pcsHeadlineInformation?.header ?? '',
      Subhead: spot?.pcsHeadlineInformation?.subHeader ?? '',
      cta: Array.isArray(spot?.pcsCta)
        ? spot.pcsCta.map((cta) => ({
            linkTitle: cta?.text ?? '',
            link: cta?.link ?? '',
          }))
        : [],
      pinnedPosition,
      textColor: spot?.textColor === '1' ? 'light' : 'dark',
      visualAssets: [[]],
      csid: '', // Temporary placeholder until we can get the csid from the AEM response
    };
  }
}
